import { localFeatureFlagOverrides, type SplitFeatureFlag } from './splitConfig';

import { useCallback } from 'react';
import { useTreatmentWithConfig } from '@splitsoftware/splitio-react';

type Treatment = {
  treatment: string;
  config: string | null;
};

type EvaluateResult = {
  treatment: string;
  isFeatureFlagEnabled: boolean;
};

type FeatureFlagResult = {
  isReady: boolean;
  /** True once the SDK has given up reaching Split.io, so callers can stop waiting. */
  hasTimedout: boolean;
  isFeatureFlagEnabled: boolean;
  treatment: string;
  config: unknown;
  evaluate: (key: string) => Promise<EvaluateResult>;
};

type UseSplitFeatureFlagOptions = {
  attributes?: SplitIO.Attributes;
  splitKey?: SplitIO.SplitKey;
};

export const useSplitFeatureFlag = (
  flag: SplitFeatureFlag,
  options?: UseSplitFeatureFlagOptions,
): FeatureFlagResult => {
  const {
    treatment: treatmentWithConfig,
    isReady,
    hasTimedout,
    factory,
  } = useTreatmentWithConfig({
    name: flag,
    ...options,
  });

  const treatment = treatmentWithConfig;
  const override = localFeatureFlagOverrides[flag];
  const isFeatureFlagEnabled = override ? override === 'on' : treatment?.treatment === 'on';

  const evaluate = useCallback(
    async (key: string): Promise<EvaluateResult> => {
      if (!factory) {
        const result = localFeatureFlagOverrides[flag] ?? 'off';
        return { treatment: result, isFeatureFlagEnabled: result === 'on' };
      }
      const keyClient = factory.client(key);
      await keyClient.whenReady();
      const result = localFeatureFlagOverrides[flag] ?? keyClient.getTreatment(flag);
      return {
        treatment: result,
        isFeatureFlagEnabled: result === 'on',
      };
    },
    [factory, flag],
  );

  return {
    isReady,
    hasTimedout,
    isFeatureFlagEnabled,
    treatment: override ?? treatment?.treatment ?? 'off',
    config: treatment?.config ?? null,
    evaluate,
  };
};
